// THE single per-subject hepatic reduction shared by the safety.viz hepatic
// tools (obot.roadmap#43, safety.viz#91): one baseline value and one peak
// on-treatment value per analyte per participant, classified into its
// pretreatment and on-treatment Hy's-Law quadrants. Lifted out of
// src/hep-explorer/composite.js so that the composite view (Amirzadegan 2025
// Fig 4), the migration Sankey (Fig 3) and the ALT waterfall (Fig 5) all reduce
// the same population the same way.
//
// NOT a pure move: this file also carries the baseline correction the plan calls
// chunk 0, and that correction DOES change the shipped composite view's results.
// On the demo dataset (site/data/adbds.csv) the composite population moves from
// 295 subjects / 23 excluded to 293 / 25 — participants 01-703-1197 and
// 01-708-1236 lose their only on-treatment total-bilirubin record once the
// resolved baseline is excluded by identity, and a further handful of peak ×ULN
// values move. That is a deliberate correction of a defect, pinned by
// `HEP-CORE-002: the demo-dataset composite population` in
// tests/unit/hep-explorer/subjects.test.js so the churn is reviewable rather
// than incidental. hep-explorer's evidence baselines must be regenerated by the
// canonical Linux evidence-update run for this change.
//
// Ported faithfully from the FDA Composite-eDISH-Plot reference
// (Composite_eDISH_Model.R, public domain / MIT, DOI 10.5281/zenodo.10892050;
// Tesfaldet et al., Drug Safety 2024;47:699-710), not reverse-engineered from
// the paper:
//   * peak = max over ON-TREATMENT records only (AVISITN > 0), taken
//     independently per analyte (non-concurrent peak-to-peak),
//   * xBLN = peak on-treatment value / the subject's own baseline value.
//
// Requirement groups: HEP-COMP-*, HEP-CORE-*.

import { GROUP_NONE } from '../hep-explorer/configure.js';
import { resolveMeasureRows } from './rows.js';
import { distinctArms, resolveArmCol, resolveArmDesignation } from './arms.js';
import { classifyComposite, concernOf } from './quadrants.js';

/**
 * Table-1 baseline-jaundice cutpoint (TB ×ULN) used when the caller carries no
 * `baseline_tb_max` of its own — hep-explorer has no such setting, only
 * hep-waterfall, which owns the cohort exclusion (HWF-DATA-003).
 */
const DEFAULT_BASELINE_TB_MAX = 1;

/** New-onset-jaundice cutpoint (TB ×ULN) when the caller carries no jaundice_uln. */
const DEFAULT_JAUNDICE_ULN = 2;

/** Sort comparator for a participant's records: study day ascending, then input order. */
function dayThenIndex(a, b) {
  const da = Number.isFinite(a.__hep_day) ? a.__hep_day : Number.MAX_SAFE_INTEGER;
  const db = Number.isFinite(b.__hep_day) ? b.__hep_day : Number.MAX_SAFE_INTEGER;
  return da - db || a.__hep_index - b.__hep_index;
}

/**
 * Split one measure's records for one participant into the resolved baseline
 * record and the on-treatment set (HEP-CORE-002/003).
 *
 * Baseline resolution: the record flagged by `baseline_col` / `baseline_value`
 * when those settings are supplied, else the study-day-0 record, else the
 * earliest by day-then-input-order (mirroring deriveBaseline).
 *
 * On-treatment: a record qualifies when it is BOTH
 *   1. not the resolved baseline record — tested BY IDENTITY, not merely by
 *      day > 0, and
 *   2. strictly later than `max(0, baselineDay)` — the FDA reference's
 *      `AVISITN > 0` floor, raised to the baseline's own day when the baseline
 *      itself sits after dosing starts.
 *
 * Both halves are load-bearing and neither subsumes the other.
 *
 * The IDENTITY half: 24 of 318 participants in the demo data carry no
 * study-day-0 ALT record and fall back to an unscheduled visit, so a bare
 * `day > 0` test counts their own baseline as on-treatment. Their peak could
 * then never fall below their baseline, silently erasing exactly the "bars
 * dropping below baseline" signal the ALT waterfall (Amirzadegan 2025 Fig 5) is
 * built on.
 *
 * The `> 0` FLOOR half: negative study days are the norm in ADaM ADLB
 * (`studyday_col: 'DY'`), where screening draws land days or weeks before first
 * dose. If the floor were the baseline's own day alone, a subject whose baseline
 * resolves to a day −14 screening draw would count their day −3 PRE-DOSE
 * screening value as on-treatment — and a pre-dose spike would then be reported
 * as a treatment-emergent rise, the exact false alarm the abnormal-baseline
 * paper exists to suppress. `Math.max(0, baselineDay)` keeps both guarantees at
 * once: pre-dose records never count, and a later-flagged baseline still
 * excludes everything up to and including its own day.
 *
 * When the data carries no usable study day — or the flagged baseline has none —
 * on-treatment degrades to every record except the baseline row, so day-less
 * data still yields a peak.
 * @param {Object[]} rows One participant's cleaned records for a single measure.
 * @param {Object} [settings] Normalized settings (`baseline_col`, `baseline_value`).
 * @returns {{baselineRow: ?Object, onTreatment: Object[]}} The split.
 */
export function splitBaselineOnTreatment(rows, settings = {}) {
  const records = rows || [];
  if (!records.length) return { baselineRow: null, onTreatment: [] };
  const ordered = [...records].sort(dayThenIndex);
  const flagged = settings.baseline_col
    ? ordered.find(
        (row) => String(row[settings.baseline_col]) === String(settings.baseline_value ?? 'Y')
      )
    : null;
  const baselineRow = flagged || ordered.find((row) => row.__hep_day === 0) || ordered[0];
  const hasDay = ordered.some((row) => Number.isFinite(row.__hep_day));
  const baselineDay = baselineRow ? baselineRow.__hep_day : NaN;
  const timed = hasDay && Number.isFinite(baselineDay);
  // Never below the FDA reference's day-0 floor, even when the resolved baseline
  // is a pre-dose screening record.
  const floor = timed ? Math.max(0, baselineDay) : NaN;
  const onTreatment = ordered.filter((row) => {
    if (row === baselineRow) return false;
    if (!timed) return true;
    return Number.isFinite(row.__hep_day) && row.__hep_day > floor;
  });
  return { baselineRow, onTreatment };
}

/**
 * Reduce one measure's records for one participant to its baseline and its peak
 * on-treatment values, in both ×ULN and the data's absolute units
 * (HEP-CORE-002/003/004).
 *
 * Every peak is an INDEPENDENT maximum over the on-treatment set, matching the
 * FDA reference: peaks can fall on different visits when the reference range
 * varies across visits, and ALT's STNRHI genuinely does vary in real data
 * (verified values 32, 34, 35, 40 and 43 in the demo dataset). So the absolute
 * peak is maximised over the RAW value directly — never read off the ×ULN-
 * maximising record, which is a different record whenever the ULN moves.
 *
 * `peakBLN` is derived from THIS function's own resolved baseline
 * (`peakValue / baselineRow.__hep_value`) rather than read off the per-row
 * `__hep_relative_baseline` column. That column is filled by
 * `structureData.deriveBaseline`, which knows only the day-0-else-earliest
 * heuristic and never reads `baseline_col`/`baseline_value`; when a study flags
 * its baseline elsewhere the two disagree and the subject's ×Baseline numbers
 * end up relative to a baseline the subject is not reported as having. Dividing
 * by the resolved baseline is self-consistent by construction, and — because the
 * baseline is a single constant per subject × measure — still yields exactly the
 * independent maximum of the ×Baseline ratio.
 *
 * Returns null when there is no usable baseline (finite, positive raw value with
 * a finite ×ULN) or no finite on-treatment peak.
 * @param {Object[]} rows One participant's cleaned records for a single measure.
 * @param {Object} [settings] Normalized settings (`baseline_col`, `baseline_value`).
 * @returns {?{baselineULN: number, peakULN: number, peakBLN: number, baselineValue: number, peakValue: number, peakDay: number, baselineDay: number, uln: number}} The reduction, or null.
 */
export function reduceMeasure(rows, settings = {}) {
  if (!rows || !rows.length) return null;
  const { baselineRow, onTreatment } = splitBaselineOnTreatment(rows, settings);
  if (
    !baselineRow ||
    !Number.isFinite(baselineRow.__hep_value) ||
    !(baselineRow.__hep_value > 0) ||
    !Number.isFinite(baselineRow.__hep_relative_uln)
  ) {
    return null;
  }

  let peakULN = NaN;
  let peakValue = NaN;
  let peakDay = NaN;
  onTreatment.forEach((row) => {
    if (Number.isFinite(row.__hep_relative_uln) && !(row.__hep_relative_uln <= peakULN)) {
      peakULN = row.__hep_relative_uln;
    }
    // The absolute peak is its own maximum over the raw value (HEP-CORE-004).
    if (Number.isFinite(row.__hep_value) && !(row.__hep_value <= peakValue)) {
      peakValue = row.__hep_value;
      peakDay = row.__hep_day;
    }
  });
  if (!Number.isFinite(peakULN) || !Number.isFinite(peakValue)) return null;
  // Relative to the baseline THIS function resolved (HEP-CORE-003), never to
  // deriveBaseline's day-0-else-earliest column.
  const peakBLN = peakValue / baselineRow.__hep_value;
  return {
    baselineULN: baselineRow.__hep_relative_uln,
    peakULN,
    peakBLN,
    baselineValue: baselineRow.__hep_value,
    peakValue,
    peakDay,
    baselineDay: baselineRow.__hep_day,
    uln: baselineRow.__hep_uln
  };
}

/**
 * Build one hepatic subject per participant — THE reduction all three figures
 * share (HEP-CORE-001, HEP-COMP-001/002/003). Each subject carries:
 *
 *   * the composite view's ×ULN world: baseline ALT/BILI ×ULN, peak
 *     on-treatment ALT/BILI ×ULN and ×Baseline, the pretreatment and
 *     on-treatment quadrants and the migration concern (Fig 3, Fig 4);
 *   * the waterfall's absolute world: `baselineAlt`, `peakAlt`, `peakAltDay`,
 *     `altUln`, `baselineBili`, `peakBili` in the data's own units (Fig 5);
 *   * the arm world: `arm`, its designated `side`, `baselineJaundice` and
 *     `newOnsetJaundice`.
 *
 * The arm column joins the retained meta UNCONDITIONALLY (HEP-CORE-005): before
 * this, an arm reached a subject only if it happened to be registered as a group
 * or a filter, otherwise `raw[armCol]` was undefined and every participant
 * silently collapsed into one '(missing)' bucket.
 *
 * A participant is excluded when either analyte lacks a usable baseline or
 * on-treatment peak (graceful degradation for abnormal/missing baselines); the
 * excluded count feeds the warning note.
 * @param {Object[]} cleanRows Rows from cleanData after deriveBaseline.
 * @param {Object} settings Normalized settings.
 * @returns {{subjects: Object[], excluded: number, armCol: ?string, arms: string[], sides: Map<string, ?string>}}
 *   The subjects, the drop count, and the resolved arm designation.
 */
export function buildHepSubjects(cleanRows, settings) {
  const rows = cleanRows || [];
  const armCol = resolveArmCol(rows, settings);
  const metaCols = [
    ...settings.groups.map((group) => group.value_col),
    ...settings.filters.map((filter) => filter.value_col),
    armCol
  ].filter((col, index, all) => col && col !== GROUP_NONE && all.indexOf(col) === index);

  const jaundiceULN = Number.isFinite(Number(settings.jaundice_uln))
    ? Number(settings.jaundice_uln)
    : DEFAULT_JAUNDICE_ULN;
  const baselineTbMax = Number.isFinite(Number(settings.baseline_tb_max))
    ? Number(settings.baseline_tb_max)
    : DEFAULT_BASELINE_TB_MAX;

  const byId = new Map();
  rows.forEach((row) => {
    const id = row[settings.id_col];
    if (!byId.has(id)) byId.set(id, []);
    byId.get(id).push(row);
  });

  const subjects = [];
  let excluded = 0;
  byId.forEach((participantRows, id) => {
    const alt = reduceMeasure(resolveMeasureRows(participantRows, settings, 'ALT'), settings);
    const bili = reduceMeasure(resolveMeasureRows(participantRows, settings, 'TB'), settings);
    if (!alt || !bili) {
      excluded += 1;
      return;
    }
    const pretreatQuadrant = classifyComposite(alt.baselineULN, bili.baselineULN);
    const onTreatQuadrant = classifyComposite(alt.peakULN, bili.peakULN);
    const raw = {};
    metaCols.forEach((col) => {
      raw[col] = participantRows[0][col] === undefined ? '' : String(participantRows[0][col]);
    });
    subjects.push({
      id,
      raw,
      baselineAltULN: alt.baselineULN,
      baselineBiliULN: bili.baselineULN,
      peakAltULN: alt.peakULN,
      peakBiliULN: bili.peakULN,
      peakAltBLN: alt.peakBLN,
      peakBiliBLN: bili.peakBLN,
      pretreatQuadrant,
      onTreatQuadrant,
      concern: concernOf(pretreatQuadrant, onTreatQuadrant),
      // Absolute units for the ALT waterfall (Fig 5), where ×ULN quadrants lose
      // their meaning and the axis is the measure's own U/L.
      baselineAlt: alt.baselineValue,
      peakAlt: alt.peakValue,
      peakAltDay: alt.peakDay,
      altUln: alt.uln,
      baselineBili: bili.baselineValue,
      peakBili: bili.peakValue,
      // Both jaundice clauses stay explicit even though, inside the waterfall's
      // post-exclusion cohort, the first is implied — so the predicate stays
      // correct when apply_tb_cohort is turned off (HEP-CORE-006).
      baselineJaundice: bili.baselineULN > baselineTbMax,
      newOnsetJaundice: bili.baselineULN <= jaundiceULN && bili.peakULN > jaundiceULN,
      arm: armCol ? raw[armCol] : '',
      side: null
    });
  });

  const arms = distinctArms(subjects);
  const designation = resolveArmDesignation(arms, settings);
  const sides = designation.sides;
  subjects.forEach((subject) => {
    subject.side = sides.get(subject.arm) ?? null;
  });

  return {
    subjects,
    excluded,
    armCol,
    arms,
    sides,
    placeboArm: designation.placeboArm,
    // Surfaced by the arm-aware views as a .sv-warning: an ambiguous placebo
    // designation must be reported, never guessed (HEP-ARM-002).
    armWarning: designation.warning
  };
}

/**
 * @deprecated Since safety.viz#91 — the composite view's reduction IS the shared
 * hepatic reduction; call `buildHepSubjects` directly. Retained as an alias so
 * the shipped composite view (Tesfaldet et al., Drug Safety 2024) and its unit
 * tests keep their original name through the split. The RESULTS are not
 * unchanged: the folded-in baseline correction (see the file header) moves the
 * demo population from 295/23 to 293/25.
 * @param {Object[]} cleanRows Rows from cleanData after deriveBaseline.
 * @param {Object} settings Normalized settings.
 * @returns {{subjects: Object[], excluded: number}} The composite subjects and drop count.
 */
export function buildCompositeSubjects(cleanRows, settings) {
  return buildHepSubjects(cleanRows, settings);
}
